import styled, { css } from "styled-components";
import { FC, useRef } from "react";

import Text from "@/components/Text";
import DistributionCalendarModalCard from "./DistributionCalendarModalCard";
import Button from "@/components/Button";
import { Inner } from "@/components/Dropdown";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { ReleaseResource } from "@/types/resources";
import { DateTime } from "luxon";

interface BoxProps {
  $xPosition: "left" | "right";
  $yPosition: "bottom" | "top";
}

const Box = styled.div<BoxProps>`
  position: absolute;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 8px 22px -6px rgba(33, 29, 30, 0.12),
    0px 14px 64px -4px rgba(33, 29, 30, 0.12);
  border-radius: 4px;
  padding: 24px 16px;
  padding-top: 22px;
  width: 360px;
  z-index: 6;

  ${({ $xPosition }) =>
    $xPosition === "left" &&
    css`
      left: 0;
    `}
  ${({ $xPosition }) =>
    $xPosition === "right" &&
    css`
      right: 0;
    `}
    ${({ $yPosition }) =>
    $yPosition === "top" &&
    css`
      bottom: calc(100% + 8px);
    `}
    ${({ $yPosition }) =>
    $yPosition === "bottom" &&
    css`
      top: calc(100% + 8px);
    `}
`;

const StyledInner = styled(Inner)`
  padding: 0;
  max-height: 404px;
  margin-right: -2px;
  padding-right: 4px;
`;

const HeadingHolder = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 22px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  font-size: 14px;
  line-height: 22px;
  height: 38px;
  margin-top: 24px;
`;

interface Props {
  onClose: () => void;
  coordinates: { x: number; y: number };
  isDayBeforeToday: boolean;
  data: ReleaseResource[];
  day: DateTime;
  onOpenScheduleModal: () => void;
}

const DistributionCalendarModal: FC<Props> = ({
  onClose,
  coordinates,
  isDayBeforeToday,
  data,
  onOpenScheduleModal,
  day,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, onClose);

  function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  }

  const getXPosition = () => {
    if (getWindowDimensions().width / coordinates.x > 1.4) return "left";

    return "right";
  };

  const getYPosition = () => {
    if (getWindowDimensions().height / coordinates.y < 1.75) return "top";
    return "bottom";
  };

  return (
    <Box
      ref={ref}
      $xPosition={getXPosition()}
      $yPosition={getYPosition()}
      id={"card_" + day.toFormat("yyyyLLdd")}
    >
      <HeadingHolder>
        <Text $type="caption">{day.toFormat("cccc, d LLLL")}</Text>
        <button onClick={() => onClose()}>
          <svg
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 8L8 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18 18L8 8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </HeadingHolder>
      <StyledInner>
        {data.map((release: any) => (
          <DistributionCalendarModalCard
            key={release.id}
            release={release}
            onOpenScheduleModal={onOpenScheduleModal}
          />
        ))}
      </StyledInner>
      {!isDayBeforeToday && (
        <StyledButton
          onClick={() => {
            onOpenScheduleModal();
            onClose();
          }}
        >
          New Shedule
        </StyledButton>
      )}
    </Box>
  );
};

export default DistributionCalendarModal;
